import {
  ONBOARDING_PATHS,
  isSalesChannelConnected,
  nextOnboardingStep,
} from '@/pages/onboarding/_components/onboarding-steps';
import type { User } from '@fluenceur/types';

/**
 * How the dashboard treats the organization of the signed-in user.
 *
 * - `onFreePlan: false`: not a free organization, so the paywall rules apply
 *   (subscription, Shopify store, internal or partner bypass), unchanged.
 * - `onFreePlan: true, redirectTo: null`: a free organization that finished its
 *   onboarding; it reaches the dashboard without a subscription or a store.
 * - `onFreePlan: true, redirectTo`: a free organization still onboarding, sent
 *   to the step it stopped at.
 */
export type FreePlanAccess =
  | { onFreePlan: false }
  | { onFreePlan: true; redirectTo: string | null };

/**
 * The free plan is the date the self sign-up wrote, never an inference: an
 * organization with no subscription and no such date is a merchant whose plan
 * lapsed, and it stays behind the paywall.
 */
export const isOnFreePlan = (user: User | null | undefined): boolean =>
  !!user?.currentBrand?.freePlanStartedAt;

export function decideFreePlanAccess(user: User | null | undefined): FreePlanAccess {
  if (!isOnFreePlan(user)) return { onFreePlan: false };

  const onboarding = user?.currentBrand?.onboarding;
  if (onboarding?.completedAt) return { onFreePlan: true, redirectTo: null };

  const isStoreConnected = isSalesChannelConnected(
    onboarding?.salesChannel,
    !!user?.integrations?.shopify?.isConnected
  );

  return {
    onFreePlan: true,
    redirectTo: ONBOARDING_PATHS[nextOnboardingStep(onboarding, isStoreConnected)],
  };
}
